import { getRouteApi } from '@tanstack/react-router';
import {
  ADMIN_CONTRACT_DETAIL,
  ADMIN_CONTRACT_EDIT,
  ADMIN_CONTRACT_NEW,
  ADMIN_CONTRACTS,
  REFERRER_CONTRACT_DETAIL,
  REFERRER_CONTRACT_EDIT,
  REFERRER_CONTRACT_NEW,
  REFERRER_CONTRACTS,
} from '@/lib/constants/routes';
import {
  buildEntityPanelNavigation,
  buildListNavigation,
  buildPanelNavigation,
  type ListRoutes,
  withoutSearchKeys,
} from '@/lib/helpers/list-routes';

/**
 * Che cosa apre il form di creazione: un contratto nuovo (default), una
 * demo, la conversione di una demo o la variazione di un contratto firmato.
 * Gli ultimi due partono da una pratica esistente (`sourceId`).
 */
export const CONTRACT_FORM_MODES = [
  'contract',
  'demo',
  'conversion',
  'amendment',
] as const;

export type ContractFormMode = (typeof CONTRACT_FORM_MODES)[number];

/** Le modalità che partono da una pratica esistente. */
export function isSourcedContractMode(mode: ContractFormMode): boolean {
  return mode === 'conversion' || mode === 'amendment';
}

/**
 * Le rotte dei contratti: la stessa pagina è montata su `/admin` e
 * `/referrer`; dettaglio, creazione e modifica si aprono sopra la lista.
 */
export const CONTRACT_ROUTES = {
  admin: {
    api: getRouteApi('/admin/_layout/contracts/_layout'),
    list: ADMIN_CONTRACTS,
    detail: ADMIN_CONTRACT_DETAIL,
    create: ADMIN_CONTRACT_NEW,
    edit: ADMIN_CONTRACT_EDIT,
  } satisfies ListRoutes,
  referrer: {
    api: getRouteApi('/referrer/_layout/contracts/_layout'),
    list: REFERRER_CONTRACTS,
    detail: REFERRER_CONTRACT_DETAIL,
    create: REFERRER_CONTRACT_NEW,
    edit: REFERRER_CONTRACT_EDIT,
  } satisfies ListRoutes,
};

type ContractArea = keyof typeof CONTRACT_ROUTES;
export type ContractRoutes = (typeof CONTRACT_ROUTES)[ContractArea];

/** Parametri del form che non appartengono alla lista. */
const FORM_SEARCH_KEYS = [
  'mode',
  'sourceId',
  'commercialRequestId',
  'successorPlan',
  'successorAddons',
] as const;

/**
 * Il link di prima alla variazione da una richiesta cliente puntava alla
 * lista con `?commercialRequestId&parentContractId`: ora il form ha la sua
 * rotta, e l'indice ci rimanda (`buildLegacyAmendmentRedirect`). Da
 * rimuovere dopo il 2026-12-31, con `parentContractId` dello schema.
 */
const LEGACY_AMENDMENT_SEARCH_KEY = 'parentContractId';

/**
 * Ritorno alla lista: i filtri restano, i parametri del form (e il link di
 * prima alla variazione) no, altrimenti l'indice riaprirebbe il form.
 */
const keepListSearch = withoutSearchKeys([
  ...FORM_SEARCH_KEYS,
  LEGACY_AMENDMENT_SEARCH_KEY,
]);

export function buildContractDetailNavigation(
  routes: ContractRoutes,
  contractId: string
) {
  return buildEntityPanelNavigation(routes.detail, contractId, keepListSearch);
}

export function buildContractListNavigation(routes: ContractRoutes) {
  return buildListNavigation(routes.list, keepListSearch);
}

/** Piano del listino 2026-10 proposto al rinnovo di un piano ritirato. */
export interface ContractSuccessorPlan {
  planName: string;
  addons: readonly string[];
}

interface ContractCreateTarget {
  mode?: ContractFormMode;
  sourceId?: string;
  commercialRequestId?: string;
  /** Solo per la variazione dai rinnovi: il piano da preselezionare. */
  successor?: ContractSuccessorPlan;
}

const ADDON_SEPARATOR = ',';

/** `successorPlan` e `successorAddons` della search → il piano proposto. */
export function parseContractSuccessorPlan(search: {
  successorPlan?: string | null;
  successorAddons?: string | null;
}): ContractSuccessorPlan | undefined {
  if (!search.successorPlan) return undefined;
  return {
    planName: search.successorPlan,
    addons: (search.successorAddons ?? '')
      .split(ADDON_SEPARATOR)
      .filter(Boolean),
  };
}

/**
 * I soli parametri del form di creazione (`contract` è il default e non si
 * scrive): per aprirlo da fuori dalla lista dei contratti, senza filtri.
 */
export function buildContractCreateSearch({
  mode,
  sourceId,
  commercialRequestId,
  successor,
}: ContractCreateTarget = {}) {
  return {
    mode: mode && mode !== 'contract' ? mode : undefined,
    sourceId,
    commercialRequestId,
    successorPlan: successor?.planName,
    successorAddons: successor?.addons.length
      ? successor.addons.join(ADDON_SEPARATOR)
      : undefined,
  };
}

export function buildContractCreateNavigation(
  routes: ContractRoutes,
  target: ContractCreateTarget = {}
) {
  return buildPanelNavigation(
    routes.create,
    <TSearch>(previous: TSearch) =>
      ({
        ...(keepListSearch(previous) as object),
        ...buildContractCreateSearch(target),
      }) as TSearch
  );
}

/**
 * La modifica tiene i filtri della lista come dettaglio e creazione:
 * salvata o chiusa torna alla lista com'era (C-11, prima i filtri si
 * perdevano).
 */
export function buildContractEditNavigation(
  routes: ContractRoutes,
  contractId: string
) {
  return buildEntityPanelNavigation(routes.edit, contractId, keepListSearch);
}

interface LegacyAmendmentSearch {
  commercialRequestId?: string | null;
  parentContractId?: string | null;
}

/**
 * Il link di prima alla variazione (vedi `LEGACY_AMENDMENT_SEARCH_KEY`)
 * diventa la rotta `new` in `replace`. Da rimuovere dopo il 2026-12-31.
 */
export function buildLegacyAmendmentRedirect(
  area: ContractArea,
  { commercialRequestId, parentContractId }: LegacyAmendmentSearch
) {
  if (!commercialRequestId || !parentContractId) return null;
  return {
    to: CONTRACT_ROUTES[area].create,
    search: {
      mode: 'amendment' as const,
      sourceId: parentContractId,
      commercialRequestId,
    },
    replace: true,
  };
}
